import * as React from 'react';
import { useState } from 'react';
import styled from 'styled-components';
import ReloadButton from 'CMFW/button/ReloadButton';
import ModuleContext from 'CMFW/context/ModuleContext';

export interface IValidatorHeaderProps {
  // Core props
  id: number | string;
  name?: string;
  idLabel?: string; // Default: "Job", can be "Validation" for ConceptLinker

  // Bulk actions - pass as a rendered element
  bulkActions?: React.ReactNode;

  // Mock call configuration
  enableMockCall?: boolean;
  mockEndpoint?: string;

  // Model for reload button context
  itemsModel: any;

  // Optional custom title renderer
  customTitle?: React.ReactNode;
}

const ValidatorHeader: React.FC<IValidatorHeaderProps> = ({
  id,
  name,
  idLabel = 'Job',
  bulkActions,
  enableMockCall = false,
  mockEndpoint,
  itemsModel,
  customTitle,
}) => {
  const [mockLoading, setMockLoading] = useState<boolean>(false);

  const handleMockCall = async () => {
    if (!mockEndpoint || !id) return;

    setMockLoading(true);
    try {
      const response = await fetch(`${mockEndpoint}/${id}/mock`, {
        method: 'POST',
      });
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Mock call failed: ${response.status} - ${errorText}`);
      }
    } catch (error) {
      console.error(`Mock call error: ${(error as Error).message}`);
    } finally {
      setMockLoading(false);
    }
  };

  // Default title format
  const defaultTitle = name || `${idLabel} #${id}`;

  return (
    <Header>
      <Title>{customTitle || defaultTitle}</Title>
      <HeaderActions>
        {bulkActions}

        {enableMockCall && mockEndpoint && (
          <MockButton onClick={handleMockCall} disabled={mockLoading}>
            {mockLoading ? 'Calling...' : 'Mock Call'}
          </MockButton>
        )}

        <div>
          <ModuleContext.Provider value={itemsModel}>
            <ReloadButton />
          </ModuleContext.Provider>
        </div>
      </HeaderActions>
    </Header>
  );
};

export default ValidatorHeader;

// Styled Components
const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: white;
  border-bottom: 1px solid #e0e0e0;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 20px;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const MockButton = styled.button<{ disabled?: boolean }>`
  padding: 8px 14px;
  background-color: ${(props) => (props.disabled ? '#ccc' : '#007bff')};
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  transition: background-color 0.2s;

  &:hover:not(:disabled) {
    background-color: #0056b3;
  }
`;
